import {
  MAX_PASSWORD_LENGTH,
  MIN_PASSWORD_LENGTH,
} from '@sirius/contracts/admin'
import type { FormEvent } from 'react'
import { useState } from 'react'

import { useUpdatePassword } from '@refinedev/core'
import { useTranslation } from 'react-i18next'
import { Link, useSearchParams } from 'react-router'
import { AuthCard } from '~/components/refineUi/form/authCard'
import { InputPassword } from '~/components/refineUi/form/inputPassword'
import { Button } from '~/components/ui/button'
import { Label } from '~/components/ui/label'
import { cn } from '~/lib/utils'
import type { UpdatePasswordParams } from '~/providers/authProvider'
import {
  FORGOT_PASSWORD_PATH,
  INVALID_RESET_LINK_ERROR,
} from '~/providers/authProvider'

/** A link that cannot reset anything, with the way to a new one. */
function InvalidLink() {
  const { t } = useTranslation()
  return (
    <>
      <p
        role="alert"
        className={cn('text-sm', 'text-destructive', 'mt-6')}
      >
        {t('auth.resetPassword.invalidLink')}
      </p>
      <div className={cn('mt-6')}>
        <Link
          to={FORGOT_PASSWORD_PATH}
          className={cn('text-sm', 'text-blue-600', 'dark:text-blue-400')}
        >
          {t('auth.resetPassword.askNewLink')}
        </Link>
      </div>
    </>
  )
}

/**
 * Where the mailed reset link lands (`/reset-password?token=…`): a new
 * password for the account. Refine's `useUpdatePassword` adds the page's
 * query (the token) to what the form sends; on success the auth provider
 * sends the user to sign in.
 */
export function ResetPasswordPage() {
  const { t } = useTranslation()
  const [searchParams] = useSearchParams()
  const [password, setPassword] = useState('')
  const [confirmation, setConfirmation] = useState('')
  const [mismatch, setMismatch] = useState(false)
  const {
    mutate: resetPassword,
    isPending,
    data,
  } = useUpdatePassword<UpdatePasswordParams>()

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()

    if (password !== confirmation) {
      setMismatch(true)
      return
    }
    setMismatch(false)
    resetPassword({ password })
  }

  const token = searchParams.get('token') ?? ''
  if (token === '' || data?.error?.name === INVALID_RESET_LINK_ERROR) {
    return (
      <AuthCard
        title={t('auth.resetPassword.title')}
        description={t('auth.resetPassword.invalidDescription')}
      >
        <InvalidLink />
      </AuthCard>
    )
  }

  return (
    <AuthCard
      title={t('auth.resetPassword.title')}
      description={t('auth.resetPassword.description')}
    >
      <form onSubmit={handleSubmit}>
        <div className={cn('flex', 'flex-col', 'gap-2', 'mt-6')}>
          <Label htmlFor="password">
            {t('auth.resetPassword.newPassword')}
          </Label>
          <InputPassword
            id="password"
            autoComplete="new-password"
            minLength={MIN_PASSWORD_LENGTH}
            maxLength={MAX_PASSWORD_LENGTH}
            required
            value={password}
            onChange={(event) => setPassword(event.target.value)}
          />
        </div>
        <div className={cn('flex', 'flex-col', 'gap-2', 'mt-6')}>
          <Label htmlFor="confirmation">
            {t('auth.resetPassword.repeatPassword')}
          </Label>
          <InputPassword
            id="confirmation"
            autoComplete="new-password"
            required
            value={confirmation}
            onChange={(event) => setConfirmation(event.target.value)}
            aria-invalid={mismatch}
          />
        </div>
        {mismatch && (
          <p
            role="alert"
            className={cn('text-sm', 'text-destructive', 'mt-2')}
          >
            {t('auth.resetPassword.mismatch')}
          </p>
        )}

        <Button
          type="submit"
          size="lg"
          className={cn('w-full', 'mt-6')}
          disabled={isPending}
        >
          {t('auth.resetPassword.submit')}
        </Button>
      </form>
    </AuthCard>
  )
}
